import { ColDef, GridOptions } from '@ag-grid-community/core'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { RootState } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { formatTooltip } from '@helios/utils'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TransactionDetailKey } from '../../panel-key'
import { ExternalTransDataSource } from '../models/external-transaction-data-source'
import { ExternalTransaction } from '../models/external-transaction.model'
import {
  CwTransactionDetailsModel,
  ExternalTransactionDetailsModel,
  externalTransactionDetailsField,
} from '../models/transaction-details.model'
import { TransactionDetailService } from '../services/transaction-detail.service'

@Component({
  selector: 'hs-external-transaction-detail-grid',
  templateUrl: './external-transaction-detail-grid.component.html',
  styleUrls: ['./external-transaction-detail-grid.component.scss'],
})
export class ExternalTransactionDetailGridComponent implements OnDestroy {
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  ExternalTransactionDetailMainGridKey = `${TransactionDetailKey}-externalTransactionDetailGrid`
  rowExternalTransactionData: ExternalTransactionDetailsModel[] = []
  rowCwTransactionData: CwTransactionDetailsModel[] = []
  private readonly externalTransactionColDefs: ColDef[] =
    this.createExternalTransColumnDefs()
  externalTransactionGridOptions: GridOptions =
    this.createExternalTransactionGridOptions()

  private readonly destroySubject$ = new Subject<void>()

  @Output()
  loadingData = new EventEmitter<boolean>()

  @Input() set externalTransactionId(externalTranId: number | undefined) {
    this.rowExternalTransactionData = []
    this.rowCwTransactionData = []
    if (externalTranId) {
      this.loadingData.emit(true)
      this.loadExternalUnmatchedTransaction(externalTranId)
    }
  }

  @Input() set clearwaterTransactionId(transId: string | undefined) {
    this.rowExternalTransactionData = []
    this.rowCwTransactionData = []
    if (transId) {
      this.loadingData.emit(true)
      this.loadTransactionMatches(transId)
    }
  }

  constructor(
    readonly store: Store<RootState>,
    public transactionDetailService: TransactionDetailService,
    public toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  loadExternalUnmatchedTransaction(externalTransId: number) {
    this.transactionDetailService
      .loadExternalUnmatchedTransaction(externalTransId)
      .subscribe(externalTransaction => {
        if (externalTransaction?.source == ExternalTransDataSource.CUSTODY) {
          this.setExternalTransactionData(externalTransaction)
        }
        if (externalTransaction?.source == ExternalTransDataSource.TRADING) {
          this.setExternalTransactionData(undefined, externalTransaction)
        }
        this.loadingData.emit(false)
      })
  }

  loadTransactionMatches(transId: string) {
    this.transactionDetailService
      .loadTransactionMatches(transId)
      .subscribe(transactionMatches => {
        const transactionMap: Map<string, ExternalTransaction> = new Map(
          Object.entries<ExternalTransaction>(transactionMatches),
        )
        this.rowExternalTransactionData = []
        const transactionMatchCustody = transactionMap.get('CUSTODY')
        const transactionMatchTrading = transactionMap.get('TRADING')
        if (transactionMatchCustody || transactionMatchTrading) {
          this.setExternalTransactionData(
            transactionMatchCustody,
            transactionMatchTrading,
          )
        }
        this.loadingData.emit(false)
      })
  }

  private createExternalTransColumnDefs(): ColDef[] {
    return [
      {
        field: externalTransactionDetailsField('property'),
        width: 250,
      },
      {
        field: externalTransactionDetailsField('custodyValue'),
        width: 400,
      },
      {
        field: externalTransactionDetailsField('tradeSystemValue'),
        width: 400,
      },
    ]
  }

  private createExternalTransactionGridOptions(): GridOptions {
    return {
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: true,
        tooltipComponentParams: {
          showTooltip: () => true,
        },
      },
      enableBrowserTooltips: true,
      columnDefs: this.externalTransactionColDefs,
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        this.savedViewService.manageSavedView(
          params,
          this.ExternalTransactionDetailMainGridKey,
          this.destroySubject$,
        )
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }

  setExternalTransactionData(
    custodyData?: ExternalTransaction,
    tradingData?: ExternalTransaction,
  ) {
    this.rowExternalTransactionData = [
      {
        property: 'Ext Transaction ID',
        custodyValue: custodyData?.externalTransactionId?.toString() ?? '',
        tradeSystemValue: tradingData?.externalTransactionId?.toString() ?? '',
      },
      {
        property: 'Account ID',
        custodyValue: custodyData?.accountId?.toString() ?? '',
        tradeSystemValue: tradingData?.accountId?.toString() ?? '',
      },
      {
        property: 'Recon Date',
        custodyValue: custodyData?.reconDate ?? '',
        tradeSystemValue: tradingData?.reconDate ?? '',
      },
      {
        property: 'Security ID',
        custodyValue: custodyData?.securityId?.toString() ?? '',
        tradeSystemValue: tradingData?.securityId?.toString() ?? '',
      },
      {
        property: 'Cusip',
        custodyValue: custodyData?.cusip ?? '',
        tradeSystemValue: tradingData?.cusip ?? '',
      },
      {
        property: 'Custody Cusip',
        custodyValue: custodyData?.cusip ?? '',
        tradeSystemValue: tradingData?.cusip ?? '',
      },
      {
        property: 'ISIN',
        custodyValue: custodyData?.isin ?? '',
        tradeSystemValue: tradingData?.isin ?? '',
      },
      {
        property: 'Sedol',
        custodyValue: custodyData?.sedol?.toString() ?? '',
        tradeSystemValue: tradingData?.sedol?.toString() ?? '',
      },
      {
        property: 'Ticker',
        custodyValue: custodyData?.ticker ?? '',
        tradeSystemValue: tradingData?.ticker ?? '',
      },
      {
        property: 'Alternate ID',
        custodyValue: custodyData?.alternateId ?? '',
        tradeSystemValue: tradingData?.alternateId ?? '',
      },
      {
        property: 'Alternate ID Description',
        custodyValue: custodyData?.alternateIdDescription ?? '',
        tradeSystemValue: tradingData?.alternateIdDescription ?? '',
      },
      {
        property: 'Transaction Type',
        custodyValue: custodyData?.tranTypeName ?? '',
        tradeSystemValue: tradingData?.tranTypeName ?? '',
      },
      {
        property: 'Description',
        custodyValue: custodyData?.description ?? '',
        tradeSystemValue: tradingData?.description ?? '',
      },
      {
        property: 'Currency',
        custodyValue: custodyData?.currencyCode ?? '',
        tradeSystemValue: tradingData?.currencyCode ?? '',
      },
      {
        property: 'Data Source',
        custodyValue: custodyData?.dataSource ?? '',
        tradeSystemValue: tradingData?.dataSource ?? '',
      },
      {
        property: 'Aggregate Info',
        custodyValue: custodyData?.aggInfoJson
          ? formatTooltip(custodyData.aggInfoJson)
          : '',
        tradeSystemValue: tradingData?.aggInfoJson
          ? formatTooltip(tradingData?.aggInfoJson)
          : '',
      },
      {
        property: 'Maturity Date',
        custodyValue: custodyData?.maturityDate ?? '',
        tradeSystemValue: tradingData?.maturityDate ?? '',
      },
      {
        property: 'Entry Date',
        custodyValue: custodyData?.entryDate ?? '',
        tradeSystemValue: tradingData?.entryDate ?? '',
      },
      {
        property: 'Settle Date',
        custodyValue: custodyData?.settleDate ?? '',
        tradeSystemValue: tradingData?.settleDate ?? '',
      },
      {
        property: 'Post Date',
        custodyValue: custodyData?.postDate ?? '',
        tradeSystemValue: tradingData?.postDate ?? '',
      },
      {
        property: 'Factor',
        custodyValue: custodyData?.factor?.toString() ?? '',
        tradeSystemValue: tradingData?.factor?.toString() ?? '',
      },
      {
        property: 'Units',
        custodyValue: custodyData?.units?.toString() ?? '',
        tradeSystemValue: tradingData?.units?.toString() ?? '',
      },
      {
        property: 'Unfactorized Units',
        custodyValue: custodyData?.unfactorizedUnits?.toString() ?? '',
        tradeSystemValue: tradingData?.unfactorizedUnits?.toString() ?? '',
      },
      {
        property: 'Price Scalar',
        custodyValue: custodyData?.priceScalar?.toString() ?? '',
        tradeSystemValue: tradingData?.priceScalar?.toString() ?? '',
      },
      {
        property: 'Price',
        custodyValue: custodyData?.price?.toString() ?? '',
        tradeSystemValue: tradingData?.price?.toString() ?? '',
      },
      {
        property: 'Transfer Purchase Info',
        custodyValue: custodyData?.accountingBasesToTpi
          ? formatTooltip(custodyData?.accountingBasesToTpi)
          : '',
        tradeSystemValue: tradingData?.accountingBasesToTpi
          ? formatTooltip(tradingData?.accountingBasesToTpi)
          : '',
      },
      {
        property: 'Unscaled Price',
        custodyValue: custodyData?.unscaledPrice?.toString() ?? '',
        tradeSystemValue: tradingData?.unscaledPrice?.toString() ?? '',
      },
      {
        property: 'Accrued Interest',
        custodyValue: custodyData?.accruedAmount?.toString() ?? '',
        tradeSystemValue: tradingData?.accruedAmount?.toString() ?? '',
      },
      {
        property: 'Amount',
        custodyValue: custodyData?.amount?.toString() ?? '',
        tradeSystemValue: tradingData?.amount?.toString() ?? '',
      },
      {
        property: 'Principal',
        custodyValue: custodyData?.principal?.toString() ?? '',
        tradeSystemValue: tradingData?.principal?.toString() ?? '',
      },
      {
        property: 'Custody Transaction ID',
        custodyValue: custodyData?.custodyTransactionId ?? '',
        tradeSystemValue: tradingData?.custodyTransactionId ?? '',
      },
      {
        property: 'Commission',
        custodyValue: custodyData?.commission?.toString() ?? '',
        tradeSystemValue: tradingData?.commission?.toString() ?? '',
      },
      {
        property: 'Origination Fee',
        custodyValue: custodyData?.originationFee?.toString() ?? '',
        tradeSystemValue: tradingData?.originationFee?.toString() ?? '',
      },
      {
        property: 'Loan Prepaid Interest',
        custodyValue: custodyData?.prepaidInterest?.toString() ?? '',
        tradeSystemValue: tradingData?.prepaidInterest?.toString() ?? '',
      },
      {
        property: 'Loan Past Due Interest',
        custodyValue: custodyData?.interestDue?.toString() ?? '',
        tradeSystemValue: tradingData?.interestDue?.toString() ?? '',
      },
      {
        property: 'Fee',
        custodyValue: custodyData?.fee?.toString() ?? '',
        tradeSystemValue: tradingData?.fee?.toString() ?? '',
      },
      {
        property: 'Custody Counter Party',
        custodyValue: custodyData?.custodyCounterpartyName ?? '',
        tradeSystemValue: tradingData?.custodyCounterpartyName ?? '',
      },
      {
        property: 'Execution Type',
        custodyValue: custodyData?.executionType?.toString() ?? '',
        tradeSystemValue: tradingData?.executionType?.toString() ?? '',
      },
      {
        property: 'Counter Party',
        custodyValue: custodyData?.counterParty ?? '',
        tradeSystemValue: tradingData?.counterParty ?? '',
      },
      {
        property: 'Orig Transaction ID',
        custodyValue: custodyData?.originalTranId?.toString() ?? '',
        tradeSystemValue: tradingData?.originalTranId?.toString() ?? '',
      },
      {
        property: 'Prometheus Transaction ID',
        custodyValue: custodyData?.prometheusTransactionId?.toString() ?? '',
        tradeSystemValue:
          tradingData?.prometheusTransactionId?.toString() ?? '',
      },
      {
        property: 'Orig Prometheus Transaction ID',
        custodyValue:
          custodyData?.originalPrometheusTransactionId?.toString() ?? '',
        tradeSystemValue:
          tradingData?.originalPrometheusTransactionId?.toString() ?? '',
      },
      {
        property: 'From Units',
        custodyValue: custodyData?.fromUnits?.toString() ?? '',
        tradeSystemValue: tradingData?.fromUnits?.toString() ?? '',
      },
      {
        property: 'From Amount',
        custodyValue: custodyData?.fromAmount?.toString() ?? '',
        tradeSystemValue: tradingData?.fromAmount?.toString() ?? '',
      },
      {
        property: 'Exchange Rate',
        custodyValue: custodyData?.exchangeRate?.toString() ?? '',
        tradeSystemValue: tradingData?.exchangeRate?.toString() ?? '',
      },
      {
        property: 'From Alternate ID',
        custodyValue: custodyData?.fromAlternateId?.toString() ?? '',
        tradeSystemValue: tradingData?.fromAlternateId?.toString() ?? '',
      },
      {
        property: 'From Alternate Security ID',
        custodyValue: custodyData?.fromAlternateSecurityId?.toString() ?? '',
        tradeSystemValue:
          tradingData?.fromAlternateSecurityId?.toString() ?? '',
      },
      {
        property: 'Capitalized',
        custodyValue: custodyData?.isCapitalize?.toString() ?? '',
        tradeSystemValue: tradingData?.isCapitalize?.toString() ?? '',
      },
    ]
  }
}
